import {FC, useState} from 'react';
import {useProductOptions} from '@shopify/hydrogen';
import {ProductFrequency} from '../products/ProductFrequency.client';
import {ProductPlanType} from '~/constants/ProductPlanType';
import {ReactHeight} from 'react-height';
import clsx from 'clsx';
import Switch from '../ui/Switch.client';
import {CheckIcon} from '..';
import {ProductSavingPlanType} from '~/constants/ProductSavingPlanType';
import {IVariantTimeslot} from '~/lib/types/graphql/api';
import {convertTimezone, localTimezone} from '~/utils/tz';
import {DISPLAY_TIMEZONE, scaleWidth} from '~/utils';

interface ProductSelectPlanProps {
  isGift: boolean;
  planType: ProductPlanType;
  savingPlan: ProductSavingPlanType;
  setIsGift: (isGift: boolean) => void;
  selectedTimeslot: IVariantTimeslot | undefined;
  setPlanType: (planType: ProductPlanType) => void;
  setSavingPlan: (savingPlan: ProductSavingPlanType) => void;
  setSavingPlanPrice: any;

  frequency?: string;
  setFrequency: (_frequency: string | undefined) => void;
}

const isSavingPlanEnabled = (date: Date): boolean => {
  const day = date.getDay();
  return day >= 0 && day <= 3;
};

export const getPlans = (date: Date) => {
  const selectedDate = convertTimezone(date, {
    from: localTimezone,
    to: DISPLAY_TIMEZONE,
  });
  return [
    {
      title: ProductSavingPlanType.Saver,
      subtitle: 'Best Price',
      savings: 10,
      bookFor: 'Sun-Wed',
      reschedule: 'No rescheduling',
      enable: isSavingPlanEnabled(selectedDate),
    },
    {
      title: ProductSavingPlanType.Standard,
      subtitle: 'Best Value',
      savings: 5,
      bookFor: 'Anyday',
      reschedule: 'Up to 24 hours before',
      enable: true,
    },
    {
      title: ProductSavingPlanType.Suite,
      subtitle: 'Most Flexible',
      savings: 0,
      bookFor: 'Anyday',
      reschedule: 'Up to 12 hours before',
      enable: true,
    },
  ];
};

export const ProductSelectPlan: FC<ProductSelectPlanProps> = ({
  isGift,
  planType,
  setIsGift,
  savingPlan,
  setPlanType,
  setSavingPlan,
  setSavingPlanPrice,
  selectedTimeslot,
  frequency,
  setFrequency,
}) => {
  const {sellingPlanGroups} = useProductOptions();
  const sellingPlanGroup = sellingPlanGroups?.[0];
  const sellingPlans = sellingPlanGroup?.sellingPlans;
  const [maxHeight1, setMaxHeight1] = useState<number>(0);
  const [maxHeight2, setMaxHeight2] = useState<number>(0);
  const [maxHeight3, setMaxHeight3] = useState<number>(0);

  const hasSubscription =
    sellingPlans && sellingPlans.some((plan) => plan?.recurringDeliveries);
  const firstSubscriptionSavingPercentage =
    Array.isArray(sellingPlanGroups) && sellingPlanGroups[0]?.name === 'GBB'
      ? '15'
      : '10';

  if (!hasSubscription) return <></>;
  if (!selectedTimeslot) return <></>;

  return (
    <div>
      <div
        className="text-center tracking-tight text-acai-900"
        style={{fontSize: scaleWidth(13)}}
      >
        Take your pick! Choose the plan that suits you best.
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {getPlans(new Date(selectedTimeslot.dateTime)).map((item) => (
          <button
            key={item.title}
            className="self-start text-center"
            disabled={!item.enable}
            onClick={() => {
              setSavingPlan(item.title);
              setSavingPlanPrice(
                planType == ProductPlanType.OneTime
                  ? item.savings == 0
                    ? 0
                    : item.savings
                  : item.savings == 0
                  ? Number(firstSubscriptionSavingPercentage)
                  : Number(
                      item.savings + Number(firstSubscriptionSavingPercentage),
                    ),
              );
            }}
          >
            <div className={clsx(item.enable ? 'text-acai-900' : 'text-black')}>
              <div className="inline-block rounded-[30px] bg-white p-[6px] font-bold">
                <div className="w-full" style={{fontSize: scaleWidth(11)}}>
                  {item.subtitle}
                </div>
              </div>
              <div
                className={clsx(
                  'mt-2 rounded-md border py-2 font-bold capitalize italic leading-[18px] transition-all',
                  item.title == savingPlan
                    ? 'border-daybreak-400 bg-daybreak-400/20'
                    : 'border-clay-100 bg-clay-100',

                  !item.enable && 'opacity-30',
                )}
                style={{fontFamily: 'GT-Alpina', fontSize: scaleWidth(16)}}
              >
                {item.title}
              </div>
              <div
                className={clsx(
                  'mt-2 rounded-md border px-2 py-4 text-start transition-all',
                  item.title == savingPlan
                    ? 'border-daybreak-400 bg-daybreak-400/20'
                    : 'border-clay-100 bg-clay-100',
                  !item.enable && 'opacity-40 blur-sm',
                )}
              >
                <div
                  className="font-bold uppercase"
                  style={{fontSize: scaleWidth(11)}}
                >
                  Savings
                </div>
                <ReactHeight
                  onHeightReady={(height: number) => {
                    setMaxHeight1((oldHeight) => {
                      return Math.max(height, oldHeight);
                    });
                  }}
                >
                  <div
                    className="mt-[5px] leading-none"
                    style={{
                      minHeight: maxHeight1 > 0 ? maxHeight1 + 'px' : 'auto',
                      fontSize: scaleWidth(12),
                    }}
                  >
                    {planType == ProductPlanType.OneTime
                      ? item.savings == 0
                        ? '-'
                        : `${item.savings}% off`
                      : item.savings == 0
                      ? `${firstSubscriptionSavingPercentage}% off`
                      : `${item.savings}% + extra ${firstSubscriptionSavingPercentage}% off`}
                  </div>
                </ReactHeight>
                <div
                  className="font-bold uppercase"
                  style={{marginTop: scaleWidth(16), fontSize: scaleWidth(11)}}
                >
                  Book For
                </div>
                <ReactHeight
                  onHeightReady={(height: number) => {
                    setMaxHeight2((oldHeight) => {
                      return Math.max(height, oldHeight);
                    });
                  }}
                >
                  <div
                    className="leading-none"
                    style={{
                      minHeight: maxHeight2 > 0 ? maxHeight2 + 'px' : 'auto',
                      marginTop: scaleWidth(5),
                      fontSize: scaleWidth(12),
                    }}
                  >
                    {item.bookFor}
                  </div>
                </ReactHeight>
                <div
                  className="font-bold uppercase"
                  style={{marginTop: scaleWidth(16), fontSize: scaleWidth(11)}}
                >
                  Reschedule
                </div>
                <ReactHeight
                  onHeightReady={(height: number) => {
                    setMaxHeight3((oldHeight) => {
                      return Math.max(height, oldHeight);
                    });
                  }}
                >
                  <div
                    className="leading-none"
                    style={{
                      minHeight: maxHeight3 > 0 ? maxHeight3 + 'px' : 'auto',
                      marginTop: scaleWidth(5),
                      fontSize: scaleWidth(12),
                    }}
                  >
                    {item.reschedule}
                  </div>
                </ReactHeight>
              </div>
              {item.title == ProductSavingPlanType.Saver && !item.enable && (
                <div className="absolute top-[28%] mt-2 w-[28%] px-2 text-[13px] font-medium leading-none text-acai-900">
                  Select a Sun-Wed slot to book Saver
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      <div className="my-[16px] h-[1px] w-full bg-clay-100" />
      <div className="flex items-center justify-center gap-3">
        <button
          className={clsx(
            'rounded-full border border-acai-900 px-[10px] py-[8px] leading-none transition-all',
            planType == ProductPlanType.OneTime
              ? 'bg-acai-900 text-white'
              : 'bg-clay-100 text-acai-900',
          )}
          style={{fontSize: scaleWidth(14)}}
        >
          One-Time
        </button>
        <Switch
          checked={planType === ProductPlanType.Subscription}
          onChange={() => {
            if (planType == ProductPlanType.Subscription) {
              setPlanType(ProductPlanType.OneTime);
            } else {
              setPlanType(ProductPlanType.Subscription);
              if (isGift) setIsGift(false);
            }
          }}
        />
        <button
          className={clsx(
            'rounded-full border border-acai-900 px-[10px] py-[8px] leading-none transition-all',
            planType == ProductPlanType.Subscription
              ? 'bg-acai-900 text-white'
              : 'bg-clay-100 text-acai-900',
          )}
          style={{fontSize: scaleWidth(14)}}
        >
          Subscribe
        </button>
      </div>
      {planType === ProductPlanType.OneTime && (
        <div className="mt-4 flex items-center justify-center gap-[10px] text-[14px]">
          <p className="text-center text-red-700">
            Skip on surge pricing by switching to subscripion
          </p>
        </div>
      )}
      {planType === ProductPlanType.Subscription && (
        <div>
          <div className="my-[16px] flex flex-col gap-[7px]">
            {['No surge fees, ever', 'Cancel anytime after 2 bookings'].map(
              (feature) => (
                <div key={feature}>
                  <div className="flex items-center justify-center gap-[10px] text-[14px]">
                    <div className="flex h-[20px] w-[20px] items-center justify-center">
                      <CheckIcon />
                    </div>
                    <span className="text-acai-900">{feature}</span>
                  </div>
                </div>
              ),
            )}
          </div>
          <ProductFrequency frequency={frequency} setFrequency={setFrequency} />
        </div>
      )}
    </div>
  );
};
